import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { KeyRound, CheckCircle2, AlertCircle } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

// Public page. The doctor is signed OUT when they land here, so this must not
// sit behind ProtectedRoute.
const MESSAGES = {
  missing: "We don't recognise this link. Please check you copied the whole thing from the email.",
  expired: "This link has expired. Links are valid for 72 hours.",
  used: "This link has already been used. If that wasn't you, contact us straight away.",
  superseded: "A newer invitation was sent to you. Please use the most recent email.",
  inactive: "This account isn't active. Please contact Arborvitae.",
};

export default function ConsultantInvitePage() {
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pw, setPw] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setInfo({ state: "missing" }); setLoading(false); return; }
    api.get(`/consultant-invite/${encodeURIComponent(token)}`)
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ state: "missing" }))
      .finally(() => setLoading(false));
  }, [token]);

  const minLen = info?.min_length || 10;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== again) { setError("The two passwords do not match"); return; }
    if (pw.length < minLen) { setError(`Choose a password of at least ${minLen} characters`); return; }
    setBusy(true);
    try {
      await api.post(`/consultant-invite/${encodeURIComponent(token)}/set-password`, { password: pw });
      setDone(true);
    } catch (err) { setError(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-6">
      <Card className="w-full max-w-md border-zinc-200 shadow-none">{children}</Card>
    </div>
  );

  if (loading) return <Shell><CardContent className="py-10 text-center text-sm text-zinc-500">Checking your link...</CardContent></Shell>;

  if (done) return (
    <Shell>
      <CardContent className="py-10 text-center space-y-4">
        <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
        <p className="text-base font-medium">Your password is set.</p>
        <p className="text-sm text-zinc-500">Sign in with your email and the password you just chose.</p>
        <Button className="w-full" onClick={() => { window.location.href = "/consultant/login"; }}>Go to sign in</Button>
      </CardContent>
    </Shell>
  );

  if (info?.state !== "valid") return (
    <Shell>
      <CardContent className="py-10 text-center space-y-4">
        <AlertCircle className="h-10 w-10 text-amber-500 mx-auto" />
        <p className="text-sm text-zinc-700">{MESSAGES[info?.state] || MESSAGES.missing}</p>
        <p className="text-xs text-zinc-500">Email support@arborvitae.in and we'll send a new link.</p>
      </CardContent>
    </Shell>
  );

  return (
    <Shell>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <KeyRound className="h-4 w-4 text-emerald-600" /> Choose your password
        </CardTitle>
        <p className="text-sm text-zinc-500 pt-1">
          {info.name ? `Dr ${info.name}` : "Your account"}{info.email ? ` - ${info.email}` : ""}
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="pw">New password</Label>
            <PasswordInput id="pw" value={pw} autoFocus autoComplete="new-password"
                   onChange={(e) => setPw(e.target.value)} />
            <p className="text-xs text-zinc-500 pt-1">At least {minLen} characters.</p>
          </div>
          <div>
            <Label htmlFor="again">Confirm password</Label>
            <PasswordInput id="again" value={again} autoComplete="new-password"
                   onChange={(e) => setAgain(e.target.value)} />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? "Saving..." : "Set password"}
          </Button>
        </form>
      </CardContent>
    </Shell>
  );
}
